import {getCommonVisualizerType} from "../../../../../config";
import {WIZARD_GROUP_TYPES} from "../../../../../interfaces/wizardItemInterface";
import {CABINET_GROUP} from "../../../../kvComponent/core/constants";
import {
  getCurrentGroups,
  getCurrentWizardNavItems,
  getFeatureGroupFromWizardGroup,
} from "./lookWizardFunctions";
import {isSameVisualizerWizard} from "./lookWizardFunctions2";

export const shouldAutoSelectWizardItems = () => {
  return !!document.kvConfig.autoSelectWizardItems;
};

// Dev shortcut for opening SQFT/calculator without manually walking every wizard step.
const getFirstAllowedFilterValue = (wizardGroup, filterGroups) => {
  const filterGroup = filterGroups.find(group => group.name === wizardGroup.groupName);
  if (!filterGroup || !Array.isArray(filterGroup.values) || !filterGroup.values.length) {
    return null;
  }
  if (!Array.isArray(wizardGroup.filtersExcluded) || !wizardGroup.filtersExcluded.length) {
    return filterGroup.values[0];
  }
  return filterGroup.values.find(filterValue => !wizardGroup.filtersExcluded.includes(filterValue.id)) || null;
};

const navItemHasRelatedFeatures = (navItem) => {
  if (!navItem.part) {
    return false;
  }
  if (Array.isArray(navItem.part.dbFeature?.relatedFeatureIds) && navItem.part.dbFeature.relatedFeatureIds.length) {
    return true;
  }
  return typeof navItem.part.getFeatureRelatedFeatures === "function"
    && Array.isArray(navItem.part.getFeatureRelatedFeatures())
    && navItem.part.getFeatureRelatedFeatures().length;
};

const getPreferredAutoSelectNavItem = (navItems, featureGroup) => {
  const navItemWithRelatedFeatures = navItems.find(navItem => {
    return navItemHasRelatedFeatures(navItem);
  });

  if (navItemWithRelatedFeatures) {
    return navItemWithRelatedFeatures;
  }

  if (featureGroup && CABINET_GROUP.includes(featureGroup.partId)) {
    return navItems[1] || navItems[0];
  }

  return navItems[0];
};

const getAutoSelectedWizardValues = (wizardItem, filterGroups) => {
  const wizardSelectedValues = {};
  let previousStepGroups = [];

  for (let wizardStep = 0; wizardStep < 50; wizardStep++) {
    const currentGroups = getCurrentGroups(wizardItem, wizardStep, wizardSelectedValues, previousStepGroups);
    if (!currentGroups.length) {
      break;
    }

    const wizardGroup = currentGroups[0];
    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);

    if (currentGroups.length > 1 || wizardGroup.nodeType === WIZARD_GROUP_TYPES.custom) {
      const selectedWizardGroup = currentGroups[0];
      const selectedFeatureGroup = getFeatureGroupFromWizardGroup(selectedWizardGroup);
      wizardSelectedValues[wizardStep] = {
        wizardGroup: selectedWizardGroup,
        partId: selectedFeatureGroup ? selectedFeatureGroup.partId : ""
      };
      previousStepGroups = currentGroups;
      continue;
    }

    if (wizardGroup.nodeType === WIZARD_GROUP_TYPES.filter) {
      const selectedFilter = getFirstAllowedFilterValue(wizardGroup, filterGroups);
      wizardSelectedValues[wizardStep] = selectedFilter
        ? {wizardGroup, selectedFilter}
        : {wizardGroup};
      previousStepGroups = currentGroups;
      continue;
    }

    if (wizardGroup.nodeType === WIZARD_GROUP_TYPES.group) {
      const navItems = getCurrentWizardNavItems(
        wizardSelectedValues,
        wizardStep,
        wizardItem,
        wizardGroup,
        featureGroup,
        filterGroups
      );
      const firstNavItem = getPreferredAutoSelectNavItem(navItems, featureGroup);
      wizardSelectedValues[wizardStep] = firstNavItem && firstNavItem.part
        ? {
          wizardGroup,
          selectedFeature: firstNavItem.part,
          partId: firstNavItem.part.partId,
          singleFeature: navItems.length === 1,
        }
        : {
          wizardGroup,
          partId: featureGroup ? featureGroup.partId : ""
        };
      previousStepGroups = currentGroups;
      continue;
    }

    wizardSelectedValues[wizardStep] = {wizardGroup};
    previousStepGroups = currentGroups;
  }

  return wizardSelectedValues;
};

export const getAutoSelectedAllWizardValues = (wizardList, allWizardsValues, activeQuoteIndex, filterGroups) => {
  if (!Array.isArray(wizardList)) {
    return allWizardsValues;
  }

  const visualizer = getCommonVisualizerType();
  const preservedWizardValues = allWizardsValues.filter(wizardObject => {
    return wizardObject.quoteIndex !== activeQuoteIndex || !isSameVisualizerWizard(wizardObject, visualizer);
  });
  let isLastActiveWizardSet = preservedWizardValues.some(wizardObject => {
    return wizardObject.quoteIndex === activeQuoteIndex
      && wizardObject.isLastActiveWizard
      && isSameVisualizerWizard(wizardObject, visualizer);
  });

  return wizardList.reduce((updatedAllValues, wizardItem) => {
    if (!isSameVisualizerWizard(wizardItem, visualizer)) {
      return updatedAllValues;
    }

    const wizardSelectedValues = getAutoSelectedWizardValues(wizardItem, filterGroups);
    if (!Object.keys(wizardSelectedValues).length) {
      return updatedAllValues;
    }

    const isLastActiveWizard = !isLastActiveWizardSet;
    isLastActiveWizardSet = true;

    return [
      ...updatedAllValues,
      {
        quoteIndex: activeQuoteIndex,
        wizardItemId: wizardItem.id,
        uniqueHashId: wizardItem.uniqueHashId,
        lastVisitedStep: Object.keys(wizardSelectedValues).length,
        visualizer,
        isLastActiveWizard,
        wizardSelectedValues,
      }
    ];
  }, preservedWizardValues);
};

export const getAutoSelectedActiveWizardState = (wizardList, allWizardsValues, activeQuoteIndex) => {
  const visualizer = getCommonVisualizerType();
  const activeWizardObject = allWizardsValues.find(wizardObject => {
    return wizardObject.quoteIndex === activeQuoteIndex
      && wizardObject.isLastActiveWizard
      && isSameVisualizerWizard(wizardObject, visualizer);
  }) || allWizardsValues.find(wizardObject => {
    return wizardObject.quoteIndex === activeQuoteIndex
      && isSameVisualizerWizard(wizardObject, visualizer);
  });

  if (!activeWizardObject) {
    return null;
  }

  const wizardActiveItem = wizardList.find(wizardItem => {
    return wizardItem.id === activeWizardObject.wizardItemId;
  });

  if (!wizardActiveItem) {
    return null;
  }

  return {
    wizardActiveItem,
    wizardSelectedValues: activeWizardObject.wizardSelectedValues || {},
    wizardActiveStep: Number.isInteger(activeWizardObject.lastVisitedStep)
      ? activeWizardObject.lastVisitedStep
      : Object.keys(activeWizardObject.wizardSelectedValues || {}).length,
  };
};
